'use client';

import type { Breadcrumb } from '@eliadmin/core/src/api/products/types/product-types';
import { BreadcrumbsSchema } from '@eliadmin/core/src/components/seo/breadcrumbs-schema';
import { BREADCRUMBS_CONFIG } from '@eliadmin/core/src/constants/breadcrumbs-config';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import {
  arePathsEqual,
  getFlatBreadcrumbs,
  getSlugFromPage,
} from '@eliadmin/core/src/utils/url-utils';
import Link from '@eliadmin/core/src/components/link';
import { usePathname } from 'next/navigation';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export type BreadcrumbsProps = {
  className?: {
    wrapper?: string;
    list?: string;
    item?: string;
    itemActive?: string;
    link?: string;
  };
  homeText?: string;
  breadcrumb: Breadcrumb;
  product?: string;
};

export function Breadcrumbs({ breadcrumb, className, product }: BreadcrumbsProps) {
  const pathname = usePathname();

  const breadcrumbs = getFlatBreadcrumbs(breadcrumb);

  if (BREADCRUMBS_CONFIG.noDisplay.filter((el: string) => el === pathname).length) {
    return <nav className="hidden"></nav>;
  }

  const { t } = useTranslations();

  return (
    <>
      <BreadcrumbsSchema breadcrumb={breadcrumb} />
      <nav className={cn('w-full rounded-none', 'ea-breadcrumbs', className?.wrapper)}>
        <ol
          className={cn(
            'flex flex-row flex-wrap items-center space-x-1 text-xs text-primary sm:text-sm',
            'ea-breadcrumbs__list',
            className?.list,
          )}
        >
          <li
            className={cn(
              {
                [cn(
                  'pointer-events-none font-light text-foreground',
                  'ea_breadcrumbs__item--active',
                  className?.itemActive,
                )]: arePathsEqual(pathname, getSlugFromPage('home')),
              },
              'ea_breadcrumbs__item',
              className?.item,
            )}
          >
            <Link
              href={getSlugFromPage('home') ?? '/'}
              className={cn(
                'ea-breadcrumbs__link inline-flex min-h-6 items-center',
                className?.link,
              )}
              prefetch={false}
            >
              {t('text160')}
            </Link>
          </li>
          {breadcrumbs.map((breadcrumb, index) => (
            <li key={index} className="w-auto">
              <span className={cn('text-foreground', className?.item)}>/ </span>
              <span
                className={cn(
                  {
                    [cn(
                      'pointer-events-none font-light text-foreground',
                      'ea_breadcrumbs__item--active',
                      className?.itemActive,
                    )]: arePathsEqual(pathname, breadcrumb.slug ?? ''),
                  },
                  'ea_breadcrumbs__item',
                  className?.item,
                )}
              >
                <Link
                  href={breadcrumb.slug ?? '#'}
                  className={cn(
                    'ea-breadcrumbs__link inline-flex min-h-6 items-center',
                    className?.link,
                  )}
                  prefetch={false}
                >
                  {breadcrumb.name}
                </Link>
              </span>
            </li>
          ))}
          {product && (
            <li className="w-auto">
              <span className={cn('text-foreground', className?.item)}>/ </span>
              <span
                className={cn(
                  'pointer-events-none font-light text-foreground',
                  'ea_breadcrumbs__item--active',
                  className?.itemActive,
                  className?.item,
                )}
              >
                {product}
              </span>
            </li>
          )}
        </ol>
      </nav>
    </>
  );
}
